import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Select } from 'antd';
import validate from '../../../utils/validate';
import './style.scss';

const DROPDOWN_MIN_SPACE = 200;

const dropUpAlign = {
  points: ['bl', 'tl'],
  offset: [0, -4],
  overflow: { adjustX: 0, adjustY: 0 },
};

function CustomSelect({
  options,
  defaultValue,
  value,
  placeholder,
  onChange,
  className,
  iconClassName,
  optGroup,
  ...otherProps
}) {
  const selectRef = useRef();
  const wrapperRef = useRef();
  const [active, setActive] = useState(false);
  const [dropUp, setDropUp] = useState(false);
  const [trackedValue, setTrackedValue] = useState(value && defaultValue);

  const arrowClassName = `engage-select-icon ${iconClassName}`;
  const labelClassName = `engage-select-label ${active || trackedValue ? 'active' : ''}`;
  const selectClassName = `engage-select ${className}`;

  const icon = <FontAwesomeIcon className={arrowClassName} icon={['fas', 'chevron-down']} />;

  const handleChange = (value) => {
    setTrackedValue(value);
    onChange && onChange(value);
  };

  const handleClickLabel = () => {
    if (!active) {
      selectRef.current.focus();
    }
  };

  const handleDropdownVisibleChange = (visible) => {
    if (visible && wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      setDropUp(spaceBelow < DROPDOWN_MIN_SPACE);
    }
    setActive(visible);
  };

  useEffect(() => {
    setTrackedValue(value);
  }, [value]);

  return (
    <div className="field-container">
      <div className="engage-select-wrapper" ref={wrapperRef}>
        <span className={labelClassName} onClick={handleClickLabel}>
          {placeholder}
        </span>
        <Select
          ref={selectRef}
          className={selectClassName}
          suffixIcon={icon}
          showAction={['focus', 'click']}
          onDropdownVisibleChange={handleDropdownVisibleChange}
          onChange={handleChange}
          defaultValue={defaultValue}
          value={trackedValue}
          getPopupContainer={() => document.body}
          {...(dropUp ? { dropdownAlign: dropUpAlign } : {})}
          {...otherProps}
        >
          {optGroup
            ? options.map((item) => (
                <Select.OptGroup label={item.title}>
                  {item.options.map(({ value, displayName, flag, ...otherProps }) => (
                    <Select.Option value={value} {...otherProps}>
                      <div className="engage-select-dropdown-item">
                        {flag && <img className="mr-2" width="20px" src={flag} alt="" />}
                        {displayName}
                      </div>
                    </Select.Option>
                  ))}
                </Select.OptGroup>
              ))
            : options.map(({ value, displayName, flag, ...otherProps }) => (
                <Select.Option value={value} {...otherProps}>
                  <div className="engage-select-dropdown-item">
                    {flag && <img className="mr-2" width="20px" src={flag} alt="" />}
                    {displayName}
                  </div>
                </Select.Option>
              ))}
        </Select>
      </div>
    </div>
  );
}

export default function SelectField(props) {
  const {
    error,
    value,
    validateObj = {},
    validateRule,
    updateFieldData,
    fk,
    triggerOnChange,
    inputType,
    showError,
    ...otherProps
  } = props;

  const [formValue, setFormValue] = useState(value);
  const [errorText, setErrorText] = useState('');

  useEffect(() => {
    let initialError;
    if (!error) {
      initialError = getErrorMessage(validateObj);
    }
    updateFieldData(fk, value, initialError);
    setErrorText(initialError);
  }, []);

  useEffect(() => {
    setFormValue(value);
  }, [value]);

  const getErrorMessage = (obj) => {
    const errors = validate(obj);
    return errors ? errors[0] : null;
  };

  const handleChange = (value) => {
    const obj = { [validateRule]: value };
    const errorText = getErrorMessage(obj);
    setFormValue(value);
    setErrorText(errorText);
    updateFieldData(fk, value, errorText);
  };

  const hasError = showError && errorText;

  const errorClassName = `${hasError ? 'has-error' : ''}`;

  return (
    <div>
      <CustomSelect
        className={errorClassName}
        iconClassName={errorClassName}
        value={formValue}
        onChange={handleChange}
        {...otherProps}
      />
      {hasError && (
        <div className="field-container__error-wrapper">
          <p className="field-container__error m-0">{errorText}</p>
        </div>
      )}
    </div>
  );
}

CustomSelect.defaultProps = {
  options: [],
};
